/**
 * The 3D designer's document — what is saved with an estimate.
 *
 * A document is a floor plan (one or more rooms, each with its own walls and
 * ceiling, see calculations/plan) plus which room the 3D view was last showing.
 * It rides inside the estimate's stored JSON under a `designer` key, next to
 * the wizard state it was seeded from, and comes back on the shared-estimate
 * page and to the render worker. Everything a reader needs to rebuild the rooms
 * is in here; nothing here is derived from the wizard at read time.
 *
 * `isDesignerDocument` is the one validator for a document from storage (the
 * estimate API, the sandbox's localStorage): anything that is not shaped like a
 * document is ignored and the designer starts again from the wizard room.
 */

import { RoomDimensions, WallConfig, WizardState } from '../../types';
import { Opening, RoomSurfaces, Surface } from '../../types/sections';
import { FloorPlan, PlanRoom, isPlanFinish, planFromSingleRoom } from '../../calculations/plan';
import { CeilingProfile } from '../../calculations/ceiling/profile';
import { createFullRoomWallConfigs } from '../../calculations/dimensions';
import { roomSurfacesFromWallConfigs, ceilingSurface } from '../../calculations/sections/generator';
import { nextOpeningId } from '../../calculations/sections/openings';
import { checkOpeningPlacement } from '../../calculations/sections/validation';
import { ROOM_PRESETS } from '../../data/presets';
import { isAccessoryKind } from '../../data/slatwallAccessories';

export const DESIGNER_DOCUMENT_VERSION = 2;

export interface DesignerDocument {
  v: typeof DESIGNER_DOCUMENT_VERSION;
  plan: FloorPlan;
  /** The room the 3D view shows; null on an empty plan. */
  focusRoomId: string | null;
}

// ---------------------------------------------------------------------------
// Validation (shared with the sandbox's localStorage reader)
// ---------------------------------------------------------------------------

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export function isSurface(s: unknown): s is Surface {
  if (!s || typeof s !== 'object') return false;
  const surface = s as Surface;
  return typeof surface.id === 'string'
    && isNum(surface.width) && surface.width > 0
    && isNum(surface.height) && surface.height > 0
    && Array.isArray(surface.sections)
    && surface.sections.every((sec) =>
      sec && typeof sec.id === 'string'
      && (sec.productType === 'WCB' || sec.productType === 'SlatWall')
      && isNum(sec.x) && isNum(sec.y) && isNum(sec.width) && isNum(sec.height)
      && (sec.accessories === undefined || (Array.isArray(sec.accessories) && sec.accessories.every((a) =>
        a && typeof a.id === 'string' && isAccessoryKind(a.kind) && isNum(a.x) && isNum(a.y)))))
    && (surface.openings === undefined || (Array.isArray(surface.openings) && surface.openings.every((o) =>
      o && typeof o.id === 'string'
      && (o.kind === 'window' || o.kind === 'door' || o.kind === 'garageDoor' || o.kind === 'passage')
      && isNum(o.x) && isNum(o.y) && isNum(o.width) && isNum(o.height)
      && (o.style === undefined || typeof o.style === 'string')
      && (o.floorPinned === undefined || typeof o.floorPinned === 'boolean')
      && (o.link === undefined || typeof o.link === 'string')
      && (o.hinge === undefined || o.hinge === 'left' || o.hinge === 'right')
      && (o.swing === undefined || o.swing === 'in' || o.swing === 'out'))));
}

export function isSurfaces(r: unknown): r is RoomSurfaces {
  if (!r || typeof r !== 'object') return false;
  const room = r as RoomSurfaces;
  return Array.isArray(room.walls) && room.walls.every(isSurface)
    && (room.ceiling === undefined || isSurface(room.ceiling))
    && (room.ceilingPlanes === undefined || (typeof room.ceilingPlanes === 'object' && room.ceilingPlanes !== null
      && Object.values(room.ceilingPlanes).every(isSurface)));
}

export function isCeilingProfile(p: unknown): p is CeilingProfile {
  if (!p || typeof p !== 'object') return false;
  const c = p as CeilingProfile;
  if (c.kind === 'flat') return true;
  if (c.kind === 'shed') return (c.ridgeAxis === 'length' || c.ridgeAxis === 'width') && (c.highSide === 'start' || c.highSide === 'end') && isNum(c.rise);
  if (c.kind === 'gable') return (c.ridgeAxis === 'length' || c.ridgeAxis === 'width') && isNum(c.ridgeAt) && isNum(c.rise);
  if (c.kind === 'vaulted') return (c.ridgeAxis === 'length' || c.ridgeAxis === 'width') && isNum(c.flat) && isNum(c.rise);
  return false;
}

export function isRoom(r: unknown): r is PlanRoom {
  if (!r || typeof r !== 'object') return false;
  const room = r as PlanRoom;
  return typeof room.id === 'string' && typeof room.name === 'string'
    && isNum(room.x) && isNum(room.z)
    && isNum(room.length) && room.length > 0
    && isNum(room.width) && room.width > 0
    && isNum(room.height) && room.height > 0
    && (room.ceilingProfile === undefined || isCeilingProfile(room.ceilingProfile))
    && isSurfaces(room.surfaces) && room.surfaces.walls.length === 4;
}

export function isPlan(p: unknown): p is FloorPlan {
  if (!p || typeof p !== 'object') return false;
  const plan = p as FloorPlan;
  return Array.isArray(plan.rooms) && plan.rooms.every(isRoom)
    && new Set(plan.rooms.map((r) => r.id)).size === plan.rooms.length
    && Array.isArray(plan.openWalls)
    && plan.openWalls.every((w) => w && typeof w.a === 'string' && typeof w.b === 'string'
      && (w.from === undefined || isNum(w.from)) && (w.to === undefined || isNum(w.to))
      && (w.y === undefined || isNum(w.y)) && (w.height === undefined || isNum(w.height))
      && (w.link === undefined || typeof w.link === 'string'))
    && (plan.spaceType === undefined || typeof plan.spaceType === 'string')
    && (plan.staging === undefined || (Array.isArray(plan.staging) && plan.staging.every((p) =>
      p && typeof p.id === 'string' && typeof p.propId === 'string' && typeof p.roomId === 'string'
      && (p.kind === 'floor' || p.kind === 'slatwall' || p.kind === 'wall' || p.kind === 'accessory' || p.kind === 'ceiling' || p.kind === 'yard')
      && isNum(p.x) && isNum(p.z) && isNum(p.rotation)
      && (p.face === undefined || ['front', 'right', 'back', 'left'].includes(p.face))
      && (p.y === undefined || isNum(p.y))
      && (p.hostSectionId === undefined || typeof p.hostSectionId === 'string')
      && (p.hostAccessoryId === undefined || typeof p.hostAccessoryId === 'string')
      && (p.accessoryKind === undefined || isAccessoryKind(p.accessoryKind)))))
    && (plan.finish === undefined || isPlanFinish(plan.finish));
}

/** Is this a designer document we can open? (Anything else: start from the wizard room.) */
export function isDesignerDocument(d: unknown): d is DesignerDocument {
  if (!d || typeof d !== 'object') return false;
  const doc = d as DesignerDocument;
  if (doc.v !== DESIGNER_DOCUMENT_VERSION || !isPlan(doc.plan)) return false;
  return doc.focusRoomId === null || (typeof doc.focusRoomId === 'string' && doc.plan.rooms.some((r) => r.id === doc.focusRoomId));
}

/** A document with its focus room checked against the plan (undo can take the remembered one away). */
export function designerDocument(plan: FloorPlan, focusRoomId: string | null): DesignerDocument {
  const focus = plan.rooms.some((r) => r.id === focusRoomId) ? focusRoomId : plan.rooms[0]?.id ?? null;
  return { v: DESIGNER_DOCUMENT_VERSION, plan, focusRoomId: focus };
}

// ---------------------------------------------------------------------------
// Seeding from the wizard
// ---------------------------------------------------------------------------

/** The wizard fields the seed reads. (`EstimatorState` has all of these.) */
export type WizardRoomSource = Pick<WizardState, 'calculationMode' | 'wallsConfig' | 'ceilingConfig' | 'dimensionsConfig' | 'applicationConfig' | 'individualWalls'>;

/**
 * The room the wizard is estimating, as the engine sees it (the same rule as
 * the estimator's `buildEstimateInput`): a ceiling-only job takes its first
 * zone, a preset its preset, else the custom size; 20 × 12 × 10 when nothing
 * is set yet.
 */
export function wizardRoomDimensions(s: WizardRoomSource): RoomDimensions {
  if (s.calculationMode === 'CeilingOnly' && s.ceilingConfig.zones?.length) {
    const zone = s.ceilingConfig.zones[0];
    return { length: zone.length, width: zone.width, height: 10 };
  }
  if (s.dimensionsConfig.usePreset && s.dimensionsConfig.presetId) {
    const preset = ROOM_PRESETS.find((p) => p.id === s.dimensionsConfig.presetId);
    if (preset) return { length: preset.length, width: preset.width, height: preset.height };
  }
  if (s.dimensionsConfig.customDimensions) return s.dimensionsConfig.customDimensions;
  return { length: 20, width: 12, height: 10 };
}

const NO_GARAGE = { enabled: false, width: 0, height: 0, numDoors: 0 };

/**
 * The wizard's four walls (the wizard's own garage is left off them here: the
 * designer has the garage door as an OPENING in a full-width wall, below).
 */
function wizardWallConfigs(s: WizardRoomSource, dims: RoomDimensions): WallConfig[] {
  if (s.calculationMode === 'IndividualWalls' && s.individualWalls?.length === 4) {
    return s.individualWalls.map((w) => ({ ...w, garage: NO_GARAGE }));
  }
  const rows = s.wallsConfig.slatWallNumPanels;
  return createFullRoomWallConfigs(dims, {
    wallColor: s.wallsConfig.color,
    wallOrientation: s.wallsConfig.orientation,
    slatWall: {
      enabled: rows > 0,
      startHeight: s.wallsConfig.slatWallStartHeight,
      numPanelRows: rows,
      color: s.wallsConfig.slatWallColor,
      useCapTrim: rows > 0,
    },
    garage: { ...NO_GARAGE, onWallType: 'None' },
  });
}

/**
 * The wizard's garage door(s) as openings on the wall the wizard put them on,
 * centred, side by side. One that would not fit its wall (a door wider than
 * the wall, say) is simply not added — the wall is still right, just bare.
 */
function withGarageDoors(surfaces: RoomSurfaces, s: WizardRoomSource, dims: RoomDimensions): RoomSurfaces {
  const garage = s.calculationMode === 'FullRoom' ? s.applicationConfig.garage : null;
  if (!garage?.enabled || garage.numDoors < 1 || garage.width <= 0 || garage.height <= 0) return surfaces;
  // wall1 = Length A (front), wall2 = Width B (right): where createFullRoomWallConfigs puts it.
  const index = garage.onWallType === 'LengthWall' ? 0 : garage.onWallType === 'WidthWall' ? 1 : -1;
  if (index < 0 || !surfaces.walls[index]) return surfaces;
  const wall = surfaces.walls[index];
  const gap = garage.numDoors > 1 ? 1 : 0; // a pier between two doors
  const total = garage.numDoors * garage.width + (garage.numDoors - 1) * gap;
  const height = Math.min(garage.height, dims.height - 0.5);
  if (total > wall.width || height <= 0) return surfaces;
  let x = (wall.width - total) / 2;
  let next: Surface = wall;
  for (let i = 0; i < garage.numDoors; i++) {
    const rect = { x, y: 0, width: garage.width, height };
    const opening: Opening = { id: nextOpeningId(next, 'garageDoor'), kind: 'garageDoor', ...rect };
    if (!checkOpeningPlacement(next, rect, opening, { margin: 0 })) {
      next = { ...next, openings: [...(next.openings ?? []), opening] };
    }
    x += garage.width + gap;
  }
  return { ...surfaces, walls: surfaces.walls.map((w, i) => (i === index ? next : w)) };
}

/**
 * A fresh document from the wizard's room: its size, its walls (colour,
 * direction, SlatWall band), its ceiling if included, and its garage door(s).
 * The starting point whenever an estimate has no designer document yet.
 */
export function designerDocumentFromWizard(s: WizardRoomSource): DesignerDocument {
  const dims = wizardRoomDimensions(s);
  const surfaces = roomSurfacesFromWallConfigs(wizardWallConfigs(s, dims), dims.height);
  const includeCeiling = s.calculationMode === 'CeilingOnly' ? true : s.ceilingConfig.include;
  if (includeCeiling) surfaces.ceiling = ceilingSurface('ceiling', 'Ceiling', dims.length, dims.width, s.ceilingConfig.color);
  const plan = planFromSingleRoom(dims, withGarageDoors(surfaces, s, dims));
  return designerDocument(plan, plan.rooms[0]?.id ?? null);
}
